/**
 * Direct sign-in — CLAUDE.md §6.7. **This form authenticates nobody**: the server proxies the
 * credential to Base Portal, and nothing here compares, stores or recognises a password.
 *
 * The password lives in form state for one submit and never survives the outcome — which is why
 * the submit is a plain async handler and not `useMutation`, whose `variables` would keep it in
 * the query client.
 */

import { useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useForm } from 'react-hook-form';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { z } from 'zod';

import { Brand } from '@/components/layout/Brand';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ApiError } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { MENU_AREAS } from '@/lib/menu';
import { useI18n } from '@/lib/i18n';

const signInSchema = z.object({
  username: z.string().trim().min(1, 'Enter your username.'),
  password: z.string().min(1, 'Enter your password.'),
});

type SignInValues = z.infer<typeof signInSchema>;

interface SignInFailure {
  title: string;
  detail: string;
}

/**
 * 401 and 503 are different failures and get different words: a 401 is Base Portal saying no, a 503
 * is it not answering. Reporting the second as "wrong password" breaks a working account.
 */
function describeFailure(error: unknown): SignInFailure {
  if (error instanceof ApiError) {
    const { status, reference } = error.problem;
    const trace = reference === undefined ? '' : ` Quote reference ${reference}.`;

    if (status === 401 || status === 403) {
      return {
        title: 'That sign-in was not accepted',
        detail:
          'Check the username and password you use for Base Portal, then try again. They are the same details.',
      };
    }
    if (status === 503) {
      return {
        title: 'Sign-in is temporarily unavailable',
        detail:
          'Base Portal cannot be reached, so sign-ins cannot be checked. Try again in a few minutes — your password has not changed and does not need resetting.',
      };
    }
    if (status >= 500) {
      return {
        title: 'Sign-in could not be completed',
        detail: `Something went wrong on the server. Try again in a few minutes; if it keeps happening, tell IT.${trace}`,
      };
    }
    return {
      title: 'Sign-in could not be completed',
      detail: `${error.problem.title}${trace}`,
    };
  }

  return {
    title: 'Sign-in could not be completed',
    detail: 'WinSight could not be reached. Check your connection and try again.',
  };
}

export default function LoginPage() {
  const { text } = useI18n();
  const { status, signIn } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [failure, setFailure] = useState<SignInFailure | null>(null);

  const {
    register,
    handleSubmit,
    resetField,
    setFocus,
    formState: { errors, isSubmitting },
  } = useForm<SignInValues>({
    resolver: zodResolver(signInSchema),
    defaultValues: { username: '', password: '' },
  });

  if (status === 'authenticated') {
    return <Navigate to="/" replace />;
  }

  // Where the guard turned them away from, so signing in resumes the linked report intact (§4.4).
  const state = location.state as { from?: { pathname: string; search: string } } | null;
  const destination =
    state?.from === undefined ? '/' : `${state.from.pathname}${state.from.search}`;

  const onSubmit = handleSubmit(async (values) => {
    setFailure(null);
    try {
      await signIn({ username: values.username, password: values.password });
      void navigate(destination, { replace: true });
    } catch (error) {
      setFailure(describeFailure(error));
      resetField('password');
      setFocus('password');
    }
  });

  return (
    <div className="flex min-h-dvh flex-col lg:flex-row">
      {/* The areas, so a first-time arrival can tell from the sign-in screen what this covers. */}
      <aside className="hidden flex-col gap-14 border-r border-border bg-card p-10 lg:flex lg:w-96">
        <Brand />
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">{text('Reports, by area')}</p>
          <ul className="space-y-2">
            {MENU_AREAS.map((area) => (
              <li key={area.id} className="flex items-center gap-3 text-sm">
                <FontAwesomeIcon
                  icon={area.icon}
                  className="w-4 shrink-0 text-center text-muted-foreground"
                  aria-hidden
                />
                <span>{text(area.label)}</span>
              </li>
            ))}
          </ul>
        </div>
        <p className="mt-auto text-xs text-muted-foreground">
          Build <span className="font-mono">{__BUILD_VERSION__}</span>
        </p>
      </aside>

      <main className="flex flex-1 items-center justify-center px-4 py-10 sm:px-6">
        <div className="w-full max-w-sm space-y-8">
          <div className="space-y-3">
            <Brand className="lg:hidden" />
            <h1 className="text-2xl font-semibold tracking-tight">{text('Sign in')}</h1>
            <p className="text-sm text-muted-foreground">
              Use your Base Portal account. If you opened WinSight from Base Portal you are signed
              in already — this page is for going straight there.
            </p>
          </div>

          {failure !== null && (
            <Alert variant="destructive">
              <FontAwesomeIcon icon={faTriangleExclamation} aria-hidden />
              <AlertTitle>{failure.title}</AlertTitle>
              <AlertDescription>{failure.detail}</AlertDescription>
            </Alert>
          )}

          <form onSubmit={onSubmit} className="space-y-5" noValidate>
            <div className="space-y-2">
              <Label htmlFor="username">{text('Username')}</Label>
              <Input
                id="username"
                type="text"
                autoComplete="username"
                autoCapitalize="none"
                spellCheck={false}
                aria-invalid={errors.username !== undefined}
                aria-describedby={errors.username === undefined ? undefined : 'username-error'}
                {...register('username')}
              />
              {errors.username !== undefined && (
                <p id="username-error" className="text-sm text-destructive">
                  {errors.username.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">{text('Password')}</Label>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                aria-invalid={errors.password !== undefined}
                aria-describedby={errors.password === undefined ? undefined : 'password-error'}
                {...register('password')}
              />
              {errors.password !== undefined && (
                <p id="password-error" className="text-sm text-destructive">
                  {errors.password.message}
                </p>
              )}
            </div>

            <Button type="submit" className="w-full" disabled={isSubmitting}>
              {isSubmitting ? text('Signing in…') : text('Sign in')}
            </Button>
          </form>
        </div>
      </main>
    </div>
  );
}
